import { type FC, useId } from "react";
import { cn } from "../../lib/utils";
import { Text } from "../../primitives/Text";
import { PortaledTooltip } from "../../ui/Tooltip";
import { trackingAttrs } from "../../utils";

const MARKER_CLASS_NAME = "ml-0.5 font-mono text-[10px] font-semibold text-link";
const UNAVAILABLE_NOTE = "Source no longer available";
const TOOLTIP_STYLE = {
	borderRadius: 8,
	lineHeight: "normal",
	maxWidth: 320,
	padding: "10px 12px",
};

/**
 * The card a citation shows on hover: what kind of source, its name, and a detail line. Exported so
 * other markers in the same panel, such as an org chart's boxes, show the same card.
 */
export const CitationTooltipCard = ({
	kind,
	heading,
	detail,
	unavailable = false,
	testId,
}: {
	kind: string;
	heading?: string;
	detail?: string;
	unavailable?: boolean;
	testId?: string;
}) => (
	<div className="flex flex-col gap-2.5" data-testid={testId}>
		<Text className="tracking-wide opacity-60" type="eyebrow">
			{kind}
		</Text>
		{heading !== undefined && heading !== "" && (
			<Text className="font-semibold" type="caption">
				{heading}
				{unavailable && "*"}
			</Text>
		)}
		{detail !== undefined && detail !== "" && (
			<Text className="font-normal opacity-80" type="caption">
				{detail}
			</Text>
		)}
		{unavailable && (
			<Text className="font-normal opacity-80" type="caption">
				*{UNAVAILABLE_NOTE}
			</Text>
		)}
	</div>
);

/** The hover card's box, for a `PortaledTooltip` that renders a `CitationTooltipCard`. */
export const CITATION_TOOLTIP_PROPS = { place: "top", style: TOOLTIP_STYLE } as const;

export type CitationMarkerProps = {
	/** The number shown as `[n]`. */
	number: number;
	/** What kind of source this is, shown above the heading ("Document", "Financial statement"). */
	kind: string;
	/** The source's name. */
	heading?: string;
	/** A secondary line under the heading. */
	detail?: string;
	/** Makes the marker a button that opens the source. Without it, the marker is hover-only. */
	onOpen?: () => void;
	/**
	 * The source no longer exists: the marker is muted, starred and hover-only whatever `onOpen`
	 * is, and the tooltip keeps its heading and detail above a starred note saying so.
	 */
	unavailable?: boolean;
	className?: string;
	testId?: string;
	trackingId?: string;
};

/**
 * An inline `[n]` superscript that names its source on hover or focus. Portals its tooltip so a
 * scrolling container cannot clip it.
 */
export const CitationMarker: FC<CitationMarkerProps> = ({
	number,
	kind,
	heading,
	detail,
	onOpen,
	unavailable = false,
	className,
	testId = "citation-marker",
	trackingId,
}) => {
	const tooltipId = useId();
	const hasHeading = heading !== undefined && heading !== "";
	const hasDetail = detail !== undefined && detail !== "";
	const description = [hasDetail && detail, unavailable && UNAVAILABLE_NOTE]
		.filter(Boolean)
		.join(". ");
	const anchorProps = {
		"aria-description": description === "" ? undefined : description,
		"aria-label": hasHeading ? `${kind}: ${heading}` : kind,
		"data-testid": testId,
		"data-tooltip-id": tooltipId,
		...trackingAttrs(trackingId),
	};

	return (
		<>
			{onOpen === undefined || unavailable ? (
				<sup
					className={cn(
						MARKER_CLASS_NAME,
						"cursor-default",
						unavailable && "text-tertiary",
						className,
					)}
					tabIndex={0}
					{...anchorProps}
				>
					[{number}]{unavailable && "*"}
				</sup>
			) : (
				<sup>
					<button
						className={cn(MARKER_CLASS_NAME, "cursor-pointer hover:underline", className)}
						onClick={onOpen}
						type="button"
						{...anchorProps}
					>
						[{number}]
					</button>
				</sup>
			)}
			<PortaledTooltip id={tooltipId} {...CITATION_TOOLTIP_PROPS}>
				<CitationTooltipCard
					kind={kind}
					heading={heading}
					detail={detail}
					unavailable={unavailable}
					testId={`${testId}-tooltip`}
				/>
			</PortaledTooltip>
		</>
	);
};
